import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useMutation } from '@apollo/client';
import { resolveBestChannel } from 'channel-selection';
import { LocationType } from 'lane-shared/types/LocationType';
import calculateChannelDistance from 'lane-shared/helpers/calculateChannelDistance';
import { TENANT_TYPES } from 'constants-channel';
import { FeatureFlag } from 'constants-flags';
import { isBuildingChannel, isTenantLocationChannel } from 'lane-shared/helpers/channel';
import { byDistance } from 'lane-shared/helpers/sort';
import Storage, { StorageKeys } from 'lane-shared/helpers/Storage';
import { Channel } from 'lane-shared/types/ChannelType';
import useFlag from 'lane-shared/hooks/useFlag';
import { convertTo62 } from 'uuid-encoding';
import { setDefaultChannelMutation } from 'graphql-queries';
import { AnalyticsContext } from 'lane-shared/contexts';
import { ANALYTIC_KEYS } from 'constants-analytics';
import emitter, { EVENT_HOME_CHANNEL_AUTO_REVERTED } from 'lane-shared/helpers/emitter';

function isSubTenantLocationChannel(channel: Channel) {
  return TENANT_TYPES.includes(channel.type) && channel.isSub && channel.parent;
}

function applyDistance(channel: Channel, location?: LocationType) {
  return {
    ...channel,
    distance: location
      ? calculateChannelDistance({ ...location, channel })
      : Number.MAX_SAFE_INTEGER,
  };
}

function sortByDistance(channels: Channel[], location?: LocationType) {
  return channels.map(channel => applyDistance(channel, location)).sort(byDistance);
}

export function useBestDefaultChannel(
  channels: Channel[],
  location?: LocationType,
  defaultChannelIdFromUser?: string | null,
  isDeepLink?: boolean
) {
  const [bestDefaultChannel, setBestDefaultChannel] = useState<Channel | null>(null);
  const fallbackToFirstChannel = useFlag(FeatureFlag.ReturnFirstChannelIfDefaultChannelIsNull, false);
  const isHomeChannelSelectionEnabled = useFlag(FeatureFlag.UserHomeChannelSelection, false);
  const analytics = useContext(AnalyticsContext);

  const [clearDefaultChannel] = useMutation(setDefaultChannelMutation, {
    onError: error => {
      console.error('[home-channel] failed to clear invalid defaultChannelId server-side', error);
    },
  });

  // Stable ref so onInvalidHomeChannel callback inside useEffect doesn't need to be a dep
  const clearDefaultChannelRef = useRef(clearDefaultChannel);
  const analyticsRef = useRef(analytics);
  const defaultChannelIdFromUserRef = useRef(defaultChannelIdFromUser);

  useEffect(() => { clearDefaultChannelRef.current = clearDefaultChannel; }, [clearDefaultChannel]);
  useEffect(() => { analyticsRef.current = analytics; }, [analytics]);
  useEffect(() => { defaultChannelIdFromUserRef.current = defaultChannelIdFromUser; }, [defaultChannelIdFromUser]);

  const onInvalidHomeChannel = useCallback(() => {
    const invalidChannelId = defaultChannelIdFromUserRef.current;

    // Fire-and-forget: do not await — routing must not block on the clear mutation
    clearDefaultChannelRef.current({ variables: { channelId: null } }).catch(() => {
      // error already logged by onError above
    });

    analyticsRef.current.track(
      ANALYTIC_KEYS.ANALYTIC_HOME_CHANNEL_INVALID_PREFERENCE_CLEARED,
      {
        attemptedChannelId: invalidChannelId ?? undefined,
        action: 'auto_reverted',
      }
    );

    emitter.emit(EVENT_HOME_CHANNEL_AUTO_REVERTED, { invalidChannelId });
  }, []);

  const channelIds = channels.map(channel => channel._id).join(',');

  useEffect(() => {
    if (isHomeChannelSelectionEnabled && !isDeepLink && defaultChannelIdFromUser === undefined) {
      setBestDefaultChannel(null);

      return;
    }

    if (channels.length === 0) {
      return;
    }

    // null means "no preference set" — preserve it so resolveBestChannel distinguishes from undefined ("still loading")
    const base62DefaultChannelId =
      defaultChannelIdFromUser != null
        ? convertTo62(defaultChannelIdFromUser)
        : defaultChannelIdFromUser;

    resolveBestChannel<Channel>({
      channels,
      defaultChannelIdFromUser: isDeepLink ? undefined : base62DefaultChannelId,
      isHomeChannelSelectionEnabled: Boolean(isHomeChannelSelectionEnabled),
      fallbackToFirstChannel: Boolean(fallbackToFirstChannel),
      getStoredValue: () => Storage.getItem(StorageKeys.PRIMARY_CHANNEL),
      onInvalidHomeChannel,
      isBuildingChannel: channel => Boolean(isBuildingChannel(channel)),
      isTenantLocationChannel: channel => Boolean(isTenantLocationChannel(channel)),
      isSubTenantLocationChannel: channel => Boolean(isSubTenantLocationChannel(channel)),
      sortByDistance: list => sortByDistance(list, location),
    }).then(setBestDefaultChannel).catch(error => {
      console.error('[home-channel] resolveBestChannel failed', error);
    });
  }, [
    channelIds,
    location,
    fallbackToFirstChannel,
    isHomeChannelSelectionEnabled,
    defaultChannelIdFromUser,
    isDeepLink,
    onInvalidHomeChannel,
  ]);

  return bestDefaultChannel;
}
